// ── BankAccountForm ──
import { useState, useEffect } from "react";
import { B } from "../../../shared/theme";
import { Btn, FInput, FSelect, Field, Modal, ReadOnlyForm } from "../../../shared/ui";
import { useFreshForm } from "../../../shared/hooks";
import { useApp } from "../../../shared/appContext";

// ── The "restricted" tone ────────────────────────────────────────────────────
//
// The credentials and full-numbers sections used B.amberDark (#603700), which
// is a dark BROWN — against the cyan section headings it read as "another
// heading" rather than "handle with care". This is genuinely red-leaning
// (hue 16 vs 34) so the difference is visible at a glance.
//
// Checked rather than eyeballed: 6.07:1 on white, so the 11px uppercase heading
// and the 13px button label both clear 4.5:1 comfortably; the button's text on
// its own tint is 5.47:1; the rule and the tint stay visible against the page.
// Close to the app's error tone (#993C1D) but not the same — an error message
// beside the button still reads as a different kind of thing.
const RESTRICTED      = "#B03A0F";   // headings, the button's text and border
const RESTRICTED_TINT = "#FCF1EC";   // the button's fill
const RESTRICTED_RULE = "#E8C3B4";   // the hairline under a section heading

export const BankAccountForm = ({ initial, accountid, readOnly }) => {
    const { api, role, modal, setModal, closeModal, saving, error, saveRec, audit } = useApp();
    const isNew = !initial;
    const [f, set] = useFreshForm(initial ? {
      ...initial,
      accountnumber:"", routingnumberach:"", routingnumberwire:"", bankpw:""   // masked: blank = unchanged
    } : {
      accountid, borrowerid:"", primaryaccount:false, bankname:"",
      accountnumber:"", routingnumberach:"", routingnumberwire:"",
      lockbox:false, controlagreement:false, controlagreementtype:"", lockboxreference:"",
      accountcloseddate:"", description:"",
      bankaddressstreet1:"", bankaddressstreet2:"", bankaddresscity:"",
      bankaddressstateregion:"", bankaddresspostalcode:"", bankaddresscountry:"",
      bankurl:"", bankusername:"", bankpw:""
    }, "BankAccounts");

    const [borrowers, setBorrowers] = useState([]);
    const [revealed, setRevealed]   = useState(null);
    const [credErr, setCredErr]     = useState(null);
    const [numbersShown, setNumbersShown] = useState(false);
    const [numbersErr, setNumbersErr]     = useState(null);
    const acctId = f.accountid || accountid;

    useEffect(() => {
      if (!acctId) return;
      api.get("getBorrowersGuarantors?accountid="+acctId).then(setBorrowers).catch(()=>setBorrowers([]));
    }, [acctId]);

    // FSelect takes flat strings, so map label <-> id
    const bgLabel   = bg => bg.borrowerguarantorname + (bg.borrowerguarantor ? " ("+bg.borrowerguarantor+")" : "");
    const bgOptions = ["", ...borrowers.map(bgLabel)];
    const bgFromLabel = lbl => (borrowers.find(bg => bgLabel(bg) === lbl) || {}).id || "";
    const currentBgLabel = (() => {
      const bg = borrowers.find(b => b.id === f.borrowerid);
      return bg ? bgLabel(bg) : "";
    })();

    // ── Does this account have a stored password? ─────────────────────────
    //
    // `initial.haspassword` is computed by getBankAccounts and travels on the
    // LIST ROW, so it is only as fresh as the last time the list reloaded. It
    // is also the only thing deciding whether the Reveal button exists — a
    // stale false there means a password that is genuinely stored looks like no
    // password at all, with no way to tell from the screen.
    //
    // AccountPage now reloads the list after a save, which is the real fix. This
    // is the second line of it: useFreshForm has already re-read the row, so if
    // that fresh copy names a secret, believe it over the cached row. Any caller
    // that forgets to pass onDone degrades to a redundant check rather than a
    // missing button.
    const hasPassword = !!(initial && (initial.haspassword || f.bankpwsecret || f.haspassword));

    // Hoisted so the click and the Enter/Space handler are the same code.
    const revealNumbers = async () => {
      setNumbersErr(null);
      try {
        const d = await api.get("getBankNumbers?id=" + initial.id);
        set("accountnumber")(d.accountnumber || "");
        set("routingnumberach")(d.routingnumberach || "");
        set("routingnumberwire")(d.routingnumberwire || "");
        setNumbersShown(true);
      } catch (e) {
        // SAY WHAT WENT WRONG. Swallowing this would leave the fields masked
        // with no explanation, which is indistinguishable from "this account
        // has no numbers on file".
        setNumbersErr(e.message || "Could not load the full numbers.");
      }
    };

    const mask = (last4, len) => last4 ? "•".repeat(len)+last4 : "";
    const caTypeOptions = ["","Springing","Blocked"];
    // `amber` is kept as the prop name so the call sites read the same; what it
    // now selects is the restricted red above.
    const ST = ({ title, amber }) => <div style={{ gridColumn:"1/-1", fontSize:11, fontWeight:600, color:amber?RESTRICTED:B.cyanDark, textTransform:"uppercase", letterSpacing:"0.06em", marginTop:18, marginBottom:8, paddingBottom:4, borderBottom:"1.5px solid "+(amber?RESTRICTED_RULE:B.cyanLight) }}>{title}</div>;

    // The record id in the title, the same way the calendar entry modal does it
    // — an account can hold several bank accounts at the same bank, and
    // "View Bank Account" over two identical-looking forms says nothing about
    // which row is open.
    //
    // Appended only when there IS an id. "Add Bank Account ()" would be worse
    // than no id at all, and `initial?.id ?? ""` interpolated straight into the
    // string produces exactly that on a record that has no id yet.
    const recId = initial && initial.id;
    const titled = (verb) => verb + " Bank Account" + (recId ? ` (${recId})` : "");

    return <Modal title={readOnly?titled("View"):initial?titled("Edit"):"Add Bank Account"} onClose={closeModal} wide
      onEdit={readOnly&&role.canEdit?()=>{ closeModal(); setModal({type:"bankaccount-edit",rec:initial,accountid,onDone:modal.onDone}); }:null}>
      <ReadOnlyForm readOnly={readOnly}>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr", gap:"0 24px" }}>

          <ST title="Account" />
          <Field label="Borrower / Guarantor">
            <FSelect value={currentBgLabel} onChange={v=>set("borrowerid")(bgFromLabel(typeof v==="object"?v.target.value:v))} options={bgOptions} />
          </Field>
          <Field label="Bank Name"><FInput value={f.bankname||""} onChange={set("bankname")} /></Field>
          <Field label={isNew?"Account Number":"Account Number (blank = unchanged)"}>
            <FInput value={f.accountnumber||""} onChange={set("accountnumber")} placeholder={isNew?"":mask(initial.accountnumberlast4,6)} />
          </Field>
          <Field label="Account Closed Date">
            <FInput type="date" value={(f.accountcloseddate||"").slice(0,10)} onChange={set("accountcloseddate")} />
          </Field>

          <Field label={isNew?"ACH Routing #":"ACH Routing # (blank = unchanged)"}>
            <FInput value={f.routingnumberach||""} onChange={set("routingnumberach")} placeholder={isNew?"9 digits":mask(initial.routingnumberachlast4,5)} />
          </Field>
          <Field label={isNew?"Wire Routing #":"Wire Routing # (blank = unchanged)"}>
            <FInput value={f.routingnumberwire||""} onChange={set("routingnumberwire")} placeholder={isNew?"9 digits":mask(initial.routingnumberwirelast4,5)} />
          </Field>

          {/* ── Reveal, next to the numbers it reveals ────────────────────────
              A <span>, NOT a <Btn>. This cell is inside ReadOnlyForm's
              <fieldset disabled>, and a disabled fieldset disables the form
              controls inside it — button, input, select, textarea. It does not
              touch anything else, so a span with an onClick still works. That
              matters because the view-only modal is exactly where somebody
              reading a wire instruction needs this.

              It is styled in the amber "restricted" language the credentials
              block below uses, and underlined, because the first version was a
              default Btn sitting in a row of empty input boxes at the bottom of
              the form and read as another field. */}
          {!isNew && role.canViewBankNumbers && (
            // Not a <Field>: the button is its own label, and "Full numbers"
            // sitting above it read as a heading for an input that was not
            // there. paddingTop matches what a Field's label occupies (12px
            // text + 4px gap) so the button lines up with the inputs beside it
            // rather than floating above them.
            <div style={{ marginBottom:14, paddingTop:20 }}>
              {numbersShown ? (
                <div style={{ padding:"7px 0", fontSize:12, color:"var(--color-text-secondary)" }}>
                  Shown — recorded in the audit log.
                </div>
              ) : (
                /* Geometry copied from Btn (6px 12px, radius 6, 13px) so it
                   reads as a button; a real <button> here would be disabled by
                   ReadOnlyForm's fieldset, which is the whole problem. */
                <span role="button" tabIndex={0}
                  onClick={revealNumbers}
                  onKeyDown={e=>{ if (e.key==="Enter" || e.key===" ") { e.preventDefault(); revealNumbers(); } }}
                  style={{ display:"inline-block", padding:"6px 12px", borderRadius:6,
                           border:"1px solid "+RESTRICTED, background:RESTRICTED_TINT,
                           color:RESTRICTED, fontSize:13, fontWeight:600,
                           cursor:"pointer", userSelect:"none", whiteSpace:"nowrap" }}>
                  Reveal full numbers
                </span>
              )}
              {numbersErr && <div style={{ color:"#993C1D", fontSize:11, marginTop:2 }}>{numbersErr}</div>}
            </div>
          )}
          {(isNew || !role.canViewBankNumbers) && <div />}

          <div style={{ gridColumn:"1/-1", display:"flex", gap:28, marginBottom:8 }}>
            <label style={{ display:"flex", alignItems:"center", gap:8, fontSize:13, cursor:"pointer" }}>
              <input type="checkbox" checked={!!f.primaryaccount} onChange={e=>set("primaryaccount")(e.target.checked)} /> Primary Account (month-end ACH)
            </label>
          </div>

          <ST title="Lockbox & Control" />
          <div style={{ gridColumn:"1/-1", display:"flex", gap:28, marginBottom:8 }}>
            <label style={{ display:"flex", alignItems:"center", gap:8, fontSize:13, cursor:"pointer" }}>
              <input type="checkbox" checked={!!f.lockbox} onChange={e=>set("lockbox")(e.target.checked)} /> Lockbox
            </label>
            <label style={{ display:"flex", alignItems:"center", gap:8, fontSize:13, cursor:"pointer" }}>
              <input type="checkbox" checked={!!f.controlagreement} onChange={e=>{ set("controlagreement")(e.target.checked); if(!e.target.checked) set("controlagreementtype")(""); }} /> Control Agreement (DACA)
            </label>
          </div>
          <Field label="Control Agreement Type">
            <FSelect value={f.controlagreementtype||""} onChange={set("controlagreementtype")} options={caTypeOptions} />
          </Field>
          <Field label="Lockbox Reference"><FInput value={f.lockboxreference||""} onChange={set("lockboxreference")} /></Field>
          <div /><div />

          <ST title="Bank Address" />
          <Field label="Street 1"><FInput value={f.bankaddressstreet1||""} onChange={set("bankaddressstreet1")} /></Field>
          <Field label="Street 2"><FInput value={f.bankaddressstreet2||""} onChange={set("bankaddressstreet2")} /></Field>
          <Field label="City"><FInput value={f.bankaddresscity||""} onChange={set("bankaddresscity")} /></Field>
          <Field label="State / Region"><FInput value={f.bankaddressstateregion||""} onChange={set("bankaddressstateregion")} /></Field>
          <Field label="Postal Code"><FInput value={f.bankaddresspostalcode||""} onChange={set("bankaddresspostalcode")} /></Field>
          <Field label="Country"><FInput value={f.bankaddresscountry||""} onChange={set("bankaddresscountry")} /></Field>

          <div />
          <ST title="Links & Notes" />
          <Field label="Bank URL">
            {readOnly && f.bankurl
              ? <a href={f.bankurl} target="_blank" rel="noreferrer" style={{ color:B.cyan, fontSize:13 }}>{f.bankurl}</a>
              : <FInput value={f.bankurl||""} onChange={set("bankurl")} />}
          </Field>
          <Field label="Description"><FInput value={f.description||""} onChange={set("description")} /></Field>
          <div /><div />

        </div>
      </ReadOnlyForm>

      {/* ── Credentials: OUTSIDE ReadOnlyForm so Reveal/Change buttons aren't disabled by the fieldset ── */}
      {role.canViewBankCreds && (
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr", gap:"0 24px" }}>
          <ST title="Bank Credentials — Restricted" amber />
          <Field label="Bank Username">
            <FInput value={f.bankusername||""} onChange={set("bankusername")} autoComplete="off" disabled={readOnly} />
          </Field>
          <Field label={isNew?"Bank Password":"Bank Password (blank = unchanged)"}>
            {!isNew && hasPassword && revealed===null ? (
              <div style={{ display:"flex", gap:8, alignItems:"center", height:32 }}>
                <span style={{ fontFamily:"var(--font-mono)", fontSize:13, color:"var(--color-text-secondary)" }}>••••••••</span>
                <Btn onClick={async()=>{
                  setCredErr(null);
                  // This used to be `catch(e){ setRevealed(""); }` — every
                  // failure, a 403 or a Key Vault problem or a missing secret,
                  // rendered as an empty password box. Identical on screen to
                  // an account that genuinely has no password, and no way to
                  // tell which. The error is shown now.
                  try { const d = await api.get("getBankCredential?id="+initial.id); setRevealed(d.bankpw||""); }
                  catch(e){ setCredErr(e.message || "Could not read the password."); }
                }}>Reveal</Btn>
                {!readOnly && <Btn onClick={()=>setRevealed("")}>Change</Btn>}
              </div>
            ) : (
              <FInput value={revealed!==null?revealed:(f.bankpw||"")} autoComplete="new-password" disabled={readOnly}
                placeholder={!isNew ? "Enter new password (blank = unchanged)" : ""}
                onChange={v=>{ const val = typeof v==="object"?v.target.value:v; setRevealed(val); set("bankpw")(val); }} />
            )}
          </Field>
          <div style={{ gridColumn:"3 / -1", fontSize:11, alignSelf:"center", paddingTop:14 }}>
            {credErr
              ? <span style={{ color:"#993C1D" }}>{credErr}</span>
              : <span style={{ color:"var(--color-text-secondary)" }}>Credential views are recorded in the audit log.</span>}
          </div>
        </div>
      )}

      {!readOnly&&error&&<div style={{ color:"#993C1D",fontSize:12,marginBottom:8,marginTop:8 }}>{error}</div>}
      <div style={{ display:"flex",gap:8,justifyContent:"flex-end",marginTop:16 }}>
        <Btn onClick={closeModal}>{readOnly?"Close":"Cancel"}</Btn>
        {!readOnly&&<Btn disabled={saving} onClick={()=>saveRec("saveBankAccount",{...f,accountid:acctId},initial?"Edited":"Added","BankAccounts",(initial?"Updated":"Added")+" bank account",modal.onDone)} variant="primary">{saving?"Saving...":"Save"}</Btn>}
      </div>
    </Modal>;
  };
